import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://numuv.app';
const DEFAULT_IMAGE = `${SITE}/og-image.png`;

/**
 * Reusable SEO component. Sets <title>, description, canonical, OG + Twitter meta,
 * and injects JSON-LD structured data. Every marketing page should use this.
 */
export default function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  keywords,
  jsonLd,
  noIndex = false,
}) {
  const fullTitle = title ? `${title} · Numuv` : 'Numuv — AI-native recruitment platform';
  const desc = description
    || 'Numuv unifies applicants, staffing partners and hiring teams on one AI-native recruitment platform — with vector matching, autonomous AI interviews by Jay, and end-to-end pipeline automation.';
  const url = `${SITE}${path}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Numuv" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export const ORGANIZATION_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE}#organization`,
  name: 'Numuv',
  alternateName: 'Numuv AI Recruitment',
  url: SITE,
  logo: { '@type': 'ImageObject', url: `${SITE}/logo.png` },
  image: `${SITE}/og-image.png`,
  email: 'hello@numuv.app',
  sameAs: [
    'https://twitter.com/numuv',
    'https://www.linkedin.com/company/numuv',
  ],
  contactPoint: [{
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'hello@numuv.app',
    availableLanguage: ['English'],
    url: `${SITE}/contact-us`,
  }],
  knowsAbout: [
    'AI recruitment',
    'applicant tracking',
    'AI interviews',
    'vector candidate matching',
    'staffing operations',
    'resume parsing',
  ],
  description: 'AI-native recruitment platform unifying applicants, staffing organisations and hiring teams with vector matching and autonomous AI interviews.',
};

export const WEBSITE_JSONLD = {
  '@type': 'WebSite',
  '@id': `${SITE}#website`,
  name: 'Numuv',
  url: SITE,
  inLanguage: 'en',
  publisher: { '@id': `${SITE}#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE}/blog?search={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const SOFTWARE_APP_JSONLD = {
  '@type': 'SoftwareApplication',
  '@id': `${SITE}#software`,
  name: 'Numuv',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Recruitment & Applicant Tracking',
  operatingSystem: 'Web, Android, iOS',
  url: SITE,
  publisher: { '@id': `${SITE}#organization` },
  description: 'AI-native recruitment platform with vector-powered candidate matching, autonomous AI interviews by Jay, a 7-stage candidate journey pipeline, WhatsApp-native candidate workflows and usage-based Mu Points credits.',
  featureList: [
    'Vector + LLM candidate matching with match percentage and skill-gap analysis',
    'Autonomous AI voice and video interviews with transcripts and competency scoring',
    'Seven-stage candidate journey pipeline with audit history',
    'WhatsApp-native signup, resume upload, job alerts and interview prep',
    'Usage-based Mu Points credits instead of per-seat pricing',
    'Team hierarchy, permissions and CSV reporting',
    'Native Android and iOS apps with push notifications and biometric unlock',
  ],
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'INR',
    description: 'Free Starter tier. Paid usage is billed per AI action in Mu Points credits; Enterprise pricing is custom.',
    url: `${SITE}/pricing`,
  },
};

/** BreadcrumbList from [{ name, path }] pairs (Home is added automatically). */
export const breadcrumbJsonLd = (items) => ({
  '@type': 'BreadcrumbList',
  itemListElement: [{ name: 'Home', path: '/' }, ...items].map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE}${it.path}`,
  })),
});

/** FAQPage from [{ q, a }] pairs. Only use when the Q&A is visible on the page. */
export const faqJsonLd = (qa) => ({
  '@type': 'FAQPage',
  mainEntity: qa.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

/**
 * Standard page-level @graph: WebPage + breadcrumbs, tied to the site Organization.
 * `extra` accepts additional nodes (FAQPage, ItemList, Article, …).
 */
export const pageJsonLd = ({ name, description, path, type = 'WebPage', breadcrumbs = [], extra = [] }) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': type,
      '@id': `${SITE}${path}#page`,
      url: `${SITE}${path}`,
      name,
      description,
      inLanguage: 'en',
      isPartOf: { '@id': `${SITE}#website` },
      about: { '@id': `${SITE}#software` },
      publisher: { '@id': `${SITE}#organization` },
    },
    breadcrumbJsonLd(breadcrumbs),
    ...extra,
  ],
});

export { SITE };
